import { globalRequestHeaders } from "../api/getRequestHeaders";

declare global {
    interface Window {
        webkit: any;
        AppActionHandler: any;
    }
}

export interface EventData {
    action: string,
    messageID?: number,
    data?: any,
    success?: boolean
}

export interface DeviceInfo {
    identifier: string,
    platform: string,
    properties: { [key: string]: string }
}

export interface Token {
    expires_in: number
    access_token: string
}

export interface EmbeddedSurveyModel {
    type: "Enrollment" | "Survey"
    model: SurveyCompletionModel | ConsentTaskModel
}

export interface SurveyCompletionModel {
    ParticipantID: string
    surveyTemplate: any
    restorationData: any
    SurveyTemplateVersion: number
    ParticipantStateAudit: string
    SurveyID: string
    TaskID: string | null
    SurveySessionID: string
    SurveyName: string
    SurveyDisplayName: string
    WarningMessage?: string
}

export interface ConsentTaskModel {
    ParticipantID: string | null
    ParticipantIdentifier?: string
    Template: any
    Version: number
    SurveyID: string
    ParticipantStateAudit: string | null
    SurveySessionID: string
    ProjectRecordAuthorityCode: string
    ProjectID: string
}

export interface HealthConnectStatus {
    // This interface exists so that the promises used for the Health Connect
    // actions can be properly typed (vs <any> or <unknown>). The fields are
    // not included because they are not used -- the object is simply passed
    // from one layer to the next.
}

export interface HealthConnectPhrStatus {
    // This interface exists so that the promises used for the Health Connect
    // actions can be properly typed (vs <any> or <unknown>). The fields are
    // not included because they are not used -- the object is simply passed
    // from one layer to the next.
}

export class AppActionHandler {
    token: Token | null = null;
    tokenExpires: number = 0;
    isEmbedded: boolean = false;

    // See comment on setActionResult for why this is so high
    private startingMessageID = 100000;
    private currentMessageID = this.startingMessageID;
    private messageHandlers: (Function | null)[] = [];
    private messageListeners: ((data: EventData) => void)[] = [];
    private androidBackButtonHandlers: Function[] = [];
    private refreshTokenPromise: Promise<void> | null = null;
    private readonly accessTokenRenewalBufferSeconds = 120;

    private nextMessageID() {
        return this.currentMessageID++;
    }

    connect(): Promise<void> {
        const mdhd = this;
        let refreshRootAccessToken = function () {
            mdhd.token = null;

            mdhd.refreshTokenPromise = new Promise(function (resolve, reject) {
                let messageID = mdhd.nextMessageID();
                mdhd.messageHandlers[messageID] = function (tokenResponse: any) {
                    if (tokenResponse.success) {
                        mdhd.tokenExpires = Date.now() + (tokenResponse.data.expires_in * 1000);
                        mdhd.token = tokenResponse.data;
                        globalRequestHeaders["Authorization"] = "Bearer " + tokenResponse.data.access_token;
                        setTimeout(refreshRootAccessToken, ((tokenResponse.data.expires_in - mdhd.accessTokenRenewalBufferSeconds) * 1000));
                        resolve();
                    } else {
                        mdhd.token = null;
                        reject(tokenResponse.message);
                    }
                    mdhd.messageHandlers[messageID] = null;
                    mdhd.refreshTokenPromise = null;
                };

                window.webkit.messageHandlers.GetRootAccessToken.postMessage({ messageID: messageID });
            });
        };

        if (this.token && Date.now() < this.tokenExpires) {
            return Promise.resolve();
        }

        if (!this.refreshTokenPromise) {
            refreshRootAccessToken();
        }

        // Re-use promise if refresh already in progress
        return this.refreshTokenPromise!;
    }

    connectExternalAccount(externalAccountProviderID: number, options: any): Promise<void> {
        const mdhd = this;
        return new Promise<void>((resolve, reject) => {
            const messageID = mdhd.nextMessageID();
            mdhd.messageHandlers[messageID] = function (connectProviderAccountResponse: any) {
                if (connectProviderAccountResponse && connectProviderAccountResponse.success) {
                    resolve();
                } else {
                    reject(connectProviderAccountResponse.errorMessage)
                }
            };

            window.webkit.messageHandlers.ConnectExternalAccount.postMessage({
                externalAccountProviderID: externalAccountProviderID,
                messageID: messageID,
                ...options
            });
        });
    }

    getDeviceInfo(): Promise<DeviceInfo> {
        const mdhd = this;
        return new Promise<DeviceInfo>(function (resolve, reject) {
            const messageID = mdhd.nextMessageID();
            mdhd.messageHandlers[messageID] = function (deviceInfoResponse: any) {
                resolve(deviceInfoResponse.data);
            };
            window.webkit.messageHandlers.GetDeviceInfo.postMessage({ messageID: messageID });
        });
    }

    showParticipantWebVisualizationPDF(parameters: any): void {
        window.webkit.messageHandlers.ShowParticipantWebVisualizationPDF.postMessage(parameters);
    }

    openEmbeddedUrl(url: string): void {
        window.webkit.messageHandlers.OpenEmbeddedLink.postMessage(url);
    }

    openExternalUrl(url: string): void {
        window.webkit.messageHandlers.OpenExternalLink.postMessage(url);
    }

    setActionResult(data: EventData): void {
        if (!data.messageID) { return; }

        // HACK
        // iOS messages are sent from the child Web View Step iframe directly to the native app
        // However, response messages are passed through the parent frame
        // These are identified by having a messageID less than 100000; these will get passed
        // Directly back through to the child frame
        if (data.messageID < this.startingMessageID &&
            (data.action == "ConnectExternalAccountResponse" || data.action == "GetDeviceInfo")) {
            this.messageListeners.forEach(f => f(data));
        }
        if (!this.messageHandlers[data.messageID]) { return; }
        this.messageHandlers[data.messageID]!(data);
    }

    getEmbeddedSurveyInfo(): Promise<EmbeddedSurveyModel> {
        const mdhd = this;
        return new Promise<any>(function (resolve, reject) {
            const messageID = mdhd.nextMessageID();
            mdhd.messageHandlers[messageID] = function (message: any) {
                resolve(message.data);
            };
            window.webkit.messageHandlers.GetEmbeddedSurveyInfo.postMessage({ messageID: messageID });
        });
    }

    getHealthConnectStatus(): Promise<HealthConnectStatus> {
        const mdhd = this;
        return new Promise<HealthConnectStatus>(function (resolve, reject) {
            const messageID = mdhd.nextMessageID();
            mdhd.messageHandlers[messageID] = function (message: any) {
                resolve(message.data);
            };
            window.webkit.messageHandlers.HealthConnectStatus.postMessage({ messageID: messageID });
        });
    }

    showHealthConnectPrompt(): void {
        window.webkit.messageHandlers.HealthConnectPrompt.postMessage({});
    }

    getHealthConnectPhrStatus(): Promise<HealthConnectPhrStatus> {
        const mdhd = this;
        return new Promise<HealthConnectPhrStatus>(function (resolve, reject) {
            const messageID = mdhd.nextMessageID();
            mdhd.messageHandlers[messageID] = function (message: any) {
                resolve(message.data);
            };
            window.webkit.messageHandlers.HealthConnectPhrStatus.postMessage({ messageID: messageID });
        });
    }

    showHealthConnectPrhPrompt(): void {
        window.webkit.messageHandlers.HealthConnectPrhPrompt.postMessage({});
    }

    showHealthConnectSettings(): void {
        window.webkit.messageHandlers.HealthConnectSettings.postMessage({});
    }

    showHealthConnectMatchmakingPrompt(): void {
        window.webkit.messageHandlers.HealthConnectMatchmaking.postMessage({});
    }

    surveyFinished(reason: string, taskResult: any): void {
        const messageID = this.nextMessageID();
        window.webkit.messageHandlers.SurveyFinished.postMessage({ messageID: messageID, reason: reason, taskResult: taskResult });
    }

    requestSensorKitAuthorization(request: any) {
        window.webkit.messageHandlers.RequestSensorKitAuthorization.postMessage(request);
    }

    onAndroidBackButtonPressed(callback: Function): void {
        this.androidBackButtonHandlers.push(callback);
    }

    //handler for Android back button
    androidBackButtonPressed(): void {
        this.androidBackButtonHandlers.forEach(f => f());
    }

    onMessageReceived(callback: (data: EventData) => void): void {
        this.messageListeners.push(callback);
    }

    constructor() { }
}

const appActionHandler = new AppActionHandler();
window.AppActionHandler = appActionHandler;

export default appActionHandler;